/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { Modal } from 'antd';

import { useMessage } from '@/hooks/message';
import { useDeleteM2MClientMutation } from '@/hooks/mutation/useDeleteM2MClientMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type DeleteM2MClientModalProps = {
  clientId: string | undefined;
  clientName: string | undefined;
  onCancelDeletion: () => void;
  onDeleteSuccess?: () => void;
};

export function DeleteM2MClientModal({
  clientId,
  clientName,
  onCancelDeletion,
  onDeleteSuccess,
}: DeleteM2MClientModalProps) {
  const { t } = useMimicTranslation('m2mClients');
  const [message] = useMessage();

  const { mutate: deleteM2MClient, isPending } = useDeleteM2MClientMutation({
    onError: () => {
      message.error(t('deleteError'));
    },
    onSuccess: () => {
      message.success(t('clientDeleted'));
      onCancelDeletion();
      onDeleteSuccess?.();
    },
  });

  const onDelete = () => {
    if (clientId) {
      deleteM2MClient(clientId);
    }
  };

  return (
    <Modal
      open={Boolean(clientId)}
      onCancel={onCancelDeletion}
      onOk={onDelete}
      cancelText={t('confirmDelete.cancel')}
      cancelButtonProps={{ disabled: isPending }}
      okText={t('confirmDelete.ok')}
      okButtonProps={{ danger: true, loading: isPending }}
    >
      <Container padding="sm">
        <Title level={4}>{t('confirmDelete.title', { name: clientName })}</Title>
        <Text>{t('confirmDelete.body1')}</Text>
        <Text>{t('confirmDelete.body2')}</Text>
      </Container>
    </Modal>
  );
}
